/**
 * Cancel Job Modal
 *
 * Modal for canceling a job with a required reason.
 * PURELY PRESENTATIONAL - displays form and handles UI state.
 *
 * DDD Compliance:
 * - Component handles UI state only
 * - Form validation is presentation concern (required reason)
 * - Actual job cancellation handled by application service mutation
 * - Backend validates business rules and records cancellation
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useCancelJob } from '@/application/service-provider/hooks/useJobData';

interface CancelJobModalProps {
  jobId: string;
  jobName: string;
  onClose: () => void;
}

export function CancelJobModal({
  jobId,
  jobName,
  onClose,
}: CancelJobModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // Job cancellation mutation
  const cancelJob = useCancelJob();

  // Handle reason change
  const handleReasonChange = (value: string) => {
    setReason(value);
    if (error) {
      setError('');
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    if (!reason.trim()) {
      setError('Cancellation reason is required');
      return false;
    }
    if (reason.trim().length < 10) {
      setError('Please provide a more detailed reason (at least 10 characters)');
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Cancel job via mutation
      await cancelJob.mutateAsync({
        jobId,
        reason: reason.trim(),
      });

      // Close modal on success (toast is shown by mutation hook)
      onClose();
    } catch (error) {
      // Error toast is already shown by the mutation hook
      // Keep modal open so user can try again or provide different reason
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-red-900">Cancel Job</DialogTitle>
          <DialogDescription>
            Are you sure you want to cancel &quot;{jobName}&quot;? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Cancellation Reason */}
          <div>
            <label
              htmlFor="reason"
              className="block text-sm font-medium text-neutral-700 mb-2"
            >
              Cancellation Reason <span className="text-red-600">*</span>
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                error ? 'border-red-500' : 'border-neutral-300'
              }`}
              rows={4}
              maxLength={500}
              placeholder="Please explain why this job is being cancelled..."
            />
            {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
            <p className="text-sm text-neutral-500 mt-1">
              {reason.length}/500 characters
            </p>
          </div>

          {/* Warning */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-red-900 mb-1">
                  Warning
                </h4>
                <p className="text-sm text-red-700">
                  Once cancelled, this job cannot be reactivated. You will need
                  to create a new job if needed.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6 gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={cancelJob.isPending}
            >
              Keep Job
            </button>
            <button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={cancelJob.isPending}
            >
              {cancelJob.isPending ? 'Cancelling...' : 'Cancel Job'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
